import { isAuditEntityType, type AuditEntityType, type AuditEntry } from '@palaccount/contracts';

/**
 * DSH-09 §5a FIELD 5/6 — WHERE AN AUDIT ROW'S RECORD OPENS.
 *
 * PART 21 §21.13 asks that *"the entity link opens the record it names"*, and
 * until this file existed it opened nothing, for any type: the entity cell drew
 * the type as text and the id as a button that expanded the row's own detail
 * panel. An investigator reading the log could not reach the invoice.
 *
 * ── THE SHAPE IS A15'S, BECAUSE THE PROBLEM IS A15'S ───────────────────────
 *
 * `features/journals/journal-source.tsx` answers the identical question for a
 * journal entry's source document, and `features/notifications/routes.ts`
 * answers it for a notification. Both are total maps from a closed set of kinds
 * to a path or a deliberate null, with the reason for every null written at the
 * arm. This is the third, and it keeps their two rules:
 *
 *   • A KIND WHOSE RECORD IS LOOKED AT ON ONE UNAMBIGUOUS SCREEN routes to that
 *     screen, with or without an id — `result-routes.ts` settled this for
 *     search: *"FIN-01 is a TREE, not a row-per-page screen… inventing one
 *     would be inventing a screen. The chart is where an account is looked at,
 *     so the chart is where the row goes."*
 *   • A KIND WHOSE SCREEN WOULD BE A GUESS returns null and renders as text.
 *     A link that exists and goes somewhere else is the defect the rule is
 *     written about, and it looks identical to a right one on screen.
 *
 * ── AND ONE RULE THIS FILE ADDS, BECAUSE THE COLUMN IS FREE TEXT ───────────
 *
 * `audit_log.target_type` is `text` and the table is append-only, so a row can
 * legitimately carry a type NO DOOR WRITES ANY MORE. `routeForAuditEntity`
 * takes a `string` and narrows it through the roster: a type the roster does
 * not hold gets null, which the cell renders as plain text. There is no branch
 * that can produce a link to a screen nobody chose.
 *
 * ── PERMISSION IS THE DESTINATION'S JOB, NOT THE LINK'S ────────────────────
 *
 * The link is drawn for everybody who can see the row — they have already read
 * the record's type and its id. `route-permissions.ts` gates the destination on
 * arrival, exactly as it does for a search hit or a notification, and BR-PERM-51
 * is explicit that a route "must never rely on its menu entry being hidden".
 * Per-link permission logic here would be a second, weaker wall in the place
 * the spec says not to put one.
 */

/**
 * Where this row's record opens, or null when this build has nowhere honest to
 * send a reader.
 *
 * Takes the ROW rather than the type alone: `task` is written by two different
 * doors and only the event tells them apart (see its arm).
 */
export function routeForAuditEntity(
  entry: Pick<AuditEntry, 'entityType' | 'entityId' | 'event'>,
): string | null {
  const type = entry.entityType;
  if (type === null || !isAuditEntityType(type)) return null;
  return pathFor(type, entry.entityId, entry.event);
}

/**
 * The i18n key that NAMES a type on screen — the filter dropdown and the table
 * cell both call it, so the two cannot disagree about what a type is called
 * one control away (ruling D5's rule, applied to a word instead of a number).
 *
 * Takes a `string` for the same reason as above: the dropdown is built from
 * `entityTypes`, which is what the tenant's LOG holds, not what the roster
 * knows. An unrostered type falls to `audit.entity.<type>`, which no dictionary
 * answers, so the caller's `defaultValue` shows the stored name — the honest
 * rendering of a type this build has no word for.
 */
export function labelKeyForAuditEntity(entityType: string): string {
  if (isAuditEntityType(entityType)) return LABEL_KEYS[entityType];
  return `audit.entity.${entityType}`;
}

/**
 * ── THE NULLS, NAMED AND DELIBERATE ────────────────────────────────────────
 *
 * Four types are on the roster and route nowhere. Each one is a decision with
 * a reason, pinned so that the day it stops being true somebody has to change
 * this file rather than not notice — the `drawer_deposit` precedent from A15.
 *
 *   `check` — FIN-10 (incoming) and FIN-11 (outgoing) are TWO registers, and
 *     the audit row carries no direction. Only `check.create` mentions one at
 *     all, and only inside the allowlist-redacted `changes` blob, which is a
 *     DISPLAY artefact normalised on read and not a routing input; the other
 *     four cheque events (`transition`, `void`, `reverse`, `reissue`) carry
 *     nothing. So half of every such link would open the wrong register, and a
 *     reader who lands on a list that does not contain their cheque concludes
 *     the cheque is gone. A15's `routeForSource` pins `check: null` for exactly
 *     this reason. Closing it needs the direction on the row — the notification
 *     contract already splits the kind in two (`incoming_check` /
 *     `outgoing_check`), which is the shape the writers would have to adopt —
 *     and that is somebody's decision rather than this file's guess.
 *
 *   `audit_log` — the row an Export writes names THE LOG, and carries no target
 *     id at all (`audit-query.service.ts` passes none: the interesting fact is
 *     what was asked for, and it is in `changes`). A link from the audit screen
 *     back to the audit screen opens nothing the reader is not already looking
 *     at.
 *
 *   `email` — the outbox is a table with no screen. `email-dispatcher.service.ts`
 *     logs a dispatch; nothing in either web app renders `email_outbox`. Naming
 *     the type beats a link to a page that does not exist.
 *
 *   `legal_entity` — there is no screen where a legal entity is looked at. It
 *     is CHOSEN on the warehouse form (`inventory/warehouses-page.tsx`) and
 *     managed nowhere, so routing there would open a screen about warehouses to
 *     answer a question about a legal entity.
 */
function pathFor(type: AuditEntityType, id: string | null, event: string): string | null {
  switch (type) {
    // ── A page per record ──────────────────────────────────────────────────
    // Null on a missing id rather than a fall back to the list: the promise the
    // link makes is "this record", and a list is a different answer.
    case 'quotation':
      return id === null ? null : `/sales/quotes/${id}`;
    case 'sales_order':
      // SAL-03 has no read-only route; `/edit` IS where an order is opened,
      // which is where `result-routes.ts` and the opportunities screen send a
      // reader too.
      return id === null ? null : `/sales/orders/${id}/edit`;
    case 'invoice':
      return id === null ? null : `/sales/${id}`;
    case 'delivery_note':
      return id === null ? null : `/sales/deliveries/${id}`;
    case 'credit_note':
      return id === null ? null : `/sales/returns/${id}`;
    case 'purchase_order':
      return id === null ? null : `/purchases/orders/${id}`;
    case 'goods_receipt':
      return id === null ? null : `/purchases/receipts/${id}`;
    case 'import_order':
      return id === null ? null : `/purchases/imports/${id}`;
    case 'bill':
      return id === null ? null : `/purchases/bills/${id}`;
    case 'purchase_return':
      return id === null ? null : `/purchases/returns/${id}`;
    case 'receipt':
      return id === null ? null : `/accounting/receipts/${id}`;
    case 'payment':
      return id === null ? null : `/accounting/payments/${id}`;
    case 'journal_entry':
      return id === null ? null : `/accounting/journals/${id}`;
    case 'cash_transfer':
      return id === null ? null : `/banks/transfers/${id}`;
    case 'item':
      return id === null ? null : `/inventory/items/${id}`;
    case 'stock_adjustment':
      return id === null ? null : `/inventory/adjustments/${id}`;
    case 'stock_count':
      return id === null ? null : `/inventory/counts/${id}`;
    case 'stock_transfer':
      return id === null ? null : `/inventory/transfers/${id}`;
    case 'work_order':
      return id === null ? null : `/manufacturing/work-orders/${id}`;
    case 'partner':
      return id === null ? null : `/partners/${id}`;

    // FIN-09 §3 calls `?account=` "the only deep link of its kind in the
    // product", and the screen reads it. So a bank account opens ITSELF rather
    // than the first row of the list.
    case 'bank_account':
      return id === null ? '/banks/accounts' : `/banks/accounts?account=${id}`;

    // ── One screen where the record is looked at ───────────────────────────
    case 'account':
      return '/accounting/chart';
    case 'accounting_period':
      return '/accounting/periods';
    case 'currency':
    case 'revaluation':
      // FIN-05 §5d — a revaluation lives on the currencies screen, as A15 has
      // it for the journal source of the same name.
      return '/accounting/currencies';
    case 'asset':
    case 'asset_category':
    case 'depreciation_run':
      // FIN-07 is one screen carrying the register, its category defaults and
      // its runs.
      return '/accounting/assets';
    case 'einvoice_submission':
      return '/accounting/einvoicing';
    case 'reconciliation':
      // FIN-09 is a TAB of the bank-accounts screen. The row's id is a
      // reconciliation id, which no route accepts, so this is the screen rather
      // than the record — the `fixed_asset` case, and named as such rather than
      // dressed up.
      return '/banks/accounts?tab=reconciliation';
    case 'item_category':
      return '/inventory/categories';
    case 'unit':
    case 'unit_group':
      // MD-05 draws groups and their units on one screen.
      return '/inventory/units';
    case 'price_tier':
      return '/inventory/tiers';
    case 'manufacturer':
      return '/inventory/manufacturers';
    case 'warehouse':
      return '/inventory/warehouses';
    case 'stock_movement':
      // INV-04's register. (A15 pins this type null for a JOURNAL source
      // because that source never carries an id; an audit row does, and the
      // movements screen is where a movement is read.)
      return '/inventory/movements';
    case 'bom_version':
      // MFG-01 is the manufacturing landing screen.
      return '/manufacturing';
    case 'mrp_run':
      return '/manufacturing/planning';
    case 'consumption_voucher':
      return '/manufacturing/consumption';
    case 'lead':
      return '/partners/leads';
    case 'opportunity':
    case 'opp_stage':
      // CRM-02 configures its pipeline stages on the board they order.
      return '/partners/opportunities';
    case 'partner_group':
      return '/partners/groups';
    case 'employee':
      // HR-01 is one list screen; the employee record opens inside it — the
      // answer `result-routes.ts` already gives for a search hit.
      return '/employees';
    case 'job_role':
      return '/employees/roles';
    case 'leave_type':
      return '/employees/leave';
    case 'payroll_run':
      return '/employees/payroll';
    case 'candidate':
    case 'requisition':
      return '/employees/recruitment';
    case 'project':
      return '/projects';
    case 'timesheet_entry':
    case 'timesheet_week':
      return '/projects/timesheets';
    case 'notification':
      return '/notifications';
    case 'user':
      return '/admin';
    case 'role':
      return '/admin/roles';
    case 'api_key':
      return '/settings/integrations';
    case 'number_sequence':
      return '/settings/number-sequences';
    case 'payment_term':
      return '/settings/payment-terms';
    case 'data_transfer':
      return '/settings/import-export';
    case 'setting':
    case 'tenant':
      // ADM-01's General Settings is the company record's screen; `tenant` rows
      // are the company itself (signup, and the platform's own BR-OPS-00-13
      // entries written into the tenant's log).
      return '/settings';

    /**
     * TWO DOORS, ONE TYPE NAME, AND THE EVENT IS WHAT SEPARATES THEM.
     *
     * `crm/tasks.service.ts` and `projects/project-tasks.service.ts` both write
     * `targetType: 'task'`, against two different tables read on two different
     * screens. Routing every `task` row to My Tasks would send every PROJECT
     * task to a screen that cannot contain it.
     *
     * They are separable, and cheaply: the CRM door writes `task.*`
     * (`task.create`, `task.complete`, `task.reopen`, `task.cancel`,
     * `task.follow_up`) and the project door writes `project_task.*`
     * (`project_task.create`, `.update`, `.status`, `.dependency_add`,
     * `.dependency_remove`). Both families are in the closed `AUDIT_EVENTS`
     * catalogue, so the prefix is a fact about the log rather than a guess
     * about a string — which is exactly what `check` above does NOT have.
     *
     * CRM-03 lives at `/partners/followups` and opens on My Tasks, with NO id
     * in the path — the same choice `pathForNotification` makes for a task
     * reminder, and for the same reason: CRM-03 has no per-task route and no
     * panel a query string opens, so inventing `?task=<id>` that nothing reads
     * would be a link CLAIMING to open a record and not opening it. PRJ-02's
     * `/projects/tasks` is the project side's equivalent.
     */
    case 'task':
      return event.startsWith('project_task.') ? '/projects/tasks' : '/partners/followups';

    // ── Named, deliberately not linked. The reasons are in the block above. ─
    case 'check':
    case 'audit_log':
    case 'email':
    case 'legal_entity':
      return null;
  }
}

/**
 * THE NAME EACH TYPE IS SHOWN BY.
 *
 * Total over the roster, so a new type is a compile error here as well as in
 * `pathFor` — a rostered type with no name would render its own raw
 * `snake_case` into the middle of a sentence.
 *
 * ── WHY THREE PREFIXES AND NOT ONE ─────────────────────────────────────────
 *
 * Sixteen of these keys were already in `audit.entity.*` before this wave, and
 * fourteen more names already existed under `je.source.*` because A15 needed
 * the same words for a journal entry's source. Those are POINTED AT rather than
 * copied: a second key holding the same sentence is a second thing to translate
 * and a second thing to drift. The remaining thirty-five are new
 * `audit.entity.*` keys, requested in `auditlink--i18n-keys.json` and placed by
 * the orchestrator — the dictionaries are a shared table this wave is fenced
 * out of.
 */
const LABEL_KEYS: Record<AuditEntityType, string> = {
  // Already in `audit.entity.*`.
  asset: 'audit.entity.asset',
  audit_log: 'audit.entity.audit_log',
  bill: 'audit.entity.bill',
  employee: 'audit.entity.employee',
  invoice: 'audit.entity.invoice',
  item: 'audit.entity.item',
  journal_entry: 'audit.entity.journal_entry',
  opportunity: 'audit.entity.opportunity',
  partner: 'audit.entity.partner',
  payment: 'audit.entity.payment',
  project: 'audit.entity.project',
  receipt: 'audit.entity.receipt',
  role: 'audit.entity.role',
  setting: 'audit.entity.setting',
  timesheet_week: 'audit.entity.timesheet_week',
  user: 'audit.entity.user',

  // Already named by A15, under `je.source.*`. Same record, same word.
  cash_transfer: 'je.source.cash_transfer',
  check: 'je.source.check',
  credit_note: 'je.source.credit_note',
  delivery_note: 'je.source.delivery_note',
  depreciation_run: 'je.source.depreciation_run',
  goods_receipt: 'je.source.goods_receipt',
  import_order: 'je.source.import_order',
  payroll_run: 'je.source.payroll_run',
  purchase_return: 'je.source.purchase_return',
  revaluation: 'je.source.revaluation',
  stock_adjustment: 'je.source.stock_adjustment',
  stock_count: 'je.source.stock_count',
  stock_movement: 'je.source.stock_movement',
  stock_transfer: 'je.source.stock_transfer',
  work_order: 'je.source.work_order',

  // New, requested in `auditlink--i18n-keys.json`.
  account: 'audit.entity.account',
  accounting_period: 'audit.entity.accounting_period',
  api_key: 'audit.entity.api_key',
  asset_category: 'audit.entity.asset_category',
  bank_account: 'audit.entity.bank_account',
  bom_version: 'audit.entity.bom_version',
  candidate: 'audit.entity.candidate',
  consumption_voucher: 'audit.entity.consumption_voucher',
  currency: 'audit.entity.currency',
  data_transfer: 'audit.entity.data_transfer',
  einvoice_submission: 'audit.entity.einvoice_submission',
  email: 'audit.entity.email',
  item_category: 'audit.entity.item_category',
  job_role: 'audit.entity.job_role',
  lead: 'audit.entity.lead',
  legal_entity: 'audit.entity.legal_entity',
  manufacturer: 'audit.entity.manufacturer',
  leave_type: 'audit.entity.leave_type',
  mrp_run: 'audit.entity.mrp_run',
  notification: 'audit.entity.notification',
  number_sequence: 'audit.entity.number_sequence',
  opp_stage: 'audit.entity.opp_stage',
  partner_group: 'audit.entity.partner_group',
  payment_term: 'audit.entity.payment_term',
  price_tier: 'audit.entity.price_tier',
  purchase_order: 'audit.entity.purchase_order',
  quotation: 'audit.entity.quotation',
  reconciliation: 'audit.entity.reconciliation',
  requisition: 'audit.entity.requisition',
  sales_order: 'audit.entity.sales_order',
  task: 'audit.entity.task',
  tenant: 'audit.entity.tenant',
  timesheet_entry: 'audit.entity.timesheet_entry',
  unit: 'audit.entity.unit',
  unit_group: 'audit.entity.unit_group',
  warehouse: 'audit.entity.warehouse',
};
